// Modelo puro de «Cobros» del portal del vendedor (mock `portal-vendedor-cobros.html`): filas de la
// cartera, filtros, búsqueda walk-in, cupón vigente y presets del monto a cobrar.
import { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import { LoanInstallmentStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanInstallmentStatusEnum';
import type { LoanInstallmentResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanInstallmentResponse';
import type {
    LoanCreditDetail,
    SellerCreditLookupResult,
    SellerCreditRow,
} from '@/api/services/loan-credit';
import { daysBetweenDays } from '@/lib/dateHelpers';
import { installmentDueDay, isClearedInstallment, nextDueInstallment } from '@/lib/installments';

/** Lo que el silo de crédito devolvió para UN crédito. */
export interface CreditSnapshot {
    credit: LoanCreditDetail;
    installments: LoanInstallmentResponse[];
}

export type CreditRead =
    | { state: 'ready'; snapshot: CreditSnapshot }
    | { state: 'loading' }
    | { state: 'forbidden' }
    | { state: 'error' };

/** Lo que la búsqueda de la cadena trae de un crédito ajeno: solo lo necesario para cobrarlo. */
export interface WalkInRead {
    state: 'walk-in';
    lookup: SellerCreditLookupResult;
}

/** Un crédito de la lista izquierda: lo que se sabe de la venta más lo leído del crédito. */
export interface CollectionCredit {
    creditId: string;
    /** Venta que originó el crédito; `null` en el walk-in, que no sale de mis ventas. */
    saleId: string | null;
    customerName: string;
    customerCurp: string | null;
    product: string;
    imei: string | null;
    isMine: boolean;
    /** Resumen del servidor para MIS créditos; `null` en el walk-in, que se lee con el detalle. */
    summary: SellerCreditRow | null;
    /** Detalle y plan. En mis créditos se lee al abrir la fila; el walk-in trae lo de la búsqueda. */
    read: CreditRead | WalkInRead;
}

export type CollectionFilter = 'mine' | 'due-this-week' | 'arrears';

export const COLLECTION_FILTER_LABEL: Record<CollectionFilter, string> = {
    mine: 'Mis créditos',
    'due-this-week': 'Vencen esta semana',
    arrears: 'En mora',
};

export const COLLECTION_FILTERS: CollectionFilter[] = ['mine', 'due-this-week', 'arrears'];

/** Estados que ya no se cobran: ni aparecen en la cartera del vendedor. */
const CLOSED_STATUSES: readonly LoanCreditStatusEnum[] = [
    LoanCreditStatusEnum.CANCELLED,
    LoanCreditStatusEnum.DISCARDED,
    LoanCreditStatusEnum.WRITTEN_OFF,
];

/** Precio desde el que el mock rotula el equipo como gama alta ($8,000). */
export const HIGH_END_PRICE_CENTS = 800_000;

/** Hechos de cobro de un crédito, derivados de su detalle y su plan. */
export interface CreditFacts {
    status: LoanCreditStatusEnum;
    termWeeks: number;
    paidInstallments: number;
    outstandingBalanceCents: number | null;
    /** Lo mismo que «Le falta»: capital, interés e IVA. Ausente = el silo todavía no la publica. */
    totalDebtCents?: number | null;
    overdueDays: number | null;
    /** Primera cuota que todavía se debe: el «cupón vigente» del mock. */
    coupon: LoanInstallmentResponse | null;
    /** Días hasta el vencimiento del cupón: negativo = vencido hace N días. */
    couponDueIn: number | null;
    overdue: LoanInstallmentResponse[];
    /** Primera cuota pendiente que no está vencida: «el vigente» de ponerse al día. */
    currentPending: LoanInstallmentResponse | null;
    isHighEnd: boolean;
    clientLevel: string | null;
}

function isOverdue(item: LoanInstallmentResponse, today: string): boolean {
    if (isClearedInstallment(item.status)) return false;
    if (item.status === LoanInstallmentStatusEnum.OVERDUE) return true;
    return item.dueDate ? daysBetweenDays(installmentDueDay(item.dueDate), today) < 0 : false;
}

export function creditFactsOf(snapshot: CreditSnapshot, today: string): CreditFacts {
    const { credit, installments } = snapshot;
    const ordered = [...installments].sort((a, b) => a.installmentNumber - b.installmentNumber);
    const coupon = nextDueInstallment(ordered);
    const overdue = ordered.filter((item) => isOverdue(item, today));
    const currentPending =
        ordered.find((item) => !isClearedInstallment(item.status) && !isOverdue(item, today)) ??
        null;
    return {
        status: credit.status,
        termWeeks: credit.termWeeks,
        paidInstallments: credit.paidInstallments,
        outstandingBalanceCents: credit.outstandingBalanceCents,
        ...(credit.totalDebtCents === undefined ? {} : { totalDebtCents: credit.totalDebtCents }),
        overdueDays: credit.overdueDays,
        coupon,
        couponDueIn: coupon?.dueDate
            ? daysBetweenDays(installmentDueDay(coupon.dueDate), today)
            : null,
        overdue,
        currentPending,
        isHighEnd: credit.equipmentPriceCents >= HIGH_END_PRICE_CENTS,
        clientLevel: credit.clientLevelAtOrigination ?? null,
    };
}

/** Lo que la lista necesita de un crédito. Sale del resumen del servidor o del detalle ya leído. */
export type ListFacts = Pick<
    CreditFacts,
    | 'status'
    | 'termWeeks'
    | 'paidInstallments'
    | 'outstandingBalanceCents'
    | 'overdueDays'
    | 'coupon'
    | 'couponDueIn'
    | 'isHighEnd'
>;

/** Hechos de la lista desde el resumen de `GET /seller/credits`; `null` si el servidor no pudo leer el plan. */
export function summaryFactsOf(summary: SellerCreditRow, today: string): ListFacts | null {
    if (summary.summaryUnavailable) return null;
    const coupon = summary.currentInstallment;
    return {
        status: summary.status,
        termWeeks: summary.termWeeks,
        paidInstallments: summary.paidInstallments ?? 0,
        outstandingBalanceCents: summary.outstandingBalanceCents,
        overdueDays: summary.overdueDays,
        coupon,
        couponDueIn: coupon?.dueDate
            ? daysBetweenDays(installmentDueDay(coupon.dueDate), today)
            : null,
        isHighEnd: summary.equipmentPriceCents >= HIGH_END_PRICE_CENTS,
    };
}

export function factsOfRow(row: CollectionCredit, today: string): ListFacts | null {
    if (row.summary) return summaryFactsOf(row.summary, today);
    return row.read.state === 'ready' ? creditFactsOf(row.read.snapshot, today) : null;
}

export function isClosedCredit(facts: ListFacts): boolean {
    return CLOSED_STATUSES.includes(facts.status);
}

export function isInArrears(facts: ListFacts): boolean {
    return facts.status === LoanCreditStatusEnum.IN_ARREARS || (facts.overdueDays ?? 0) > 0;
}

/** ¿El crédito entra en el filtro? Sin lectura solo entra en «Mis créditos»: no se puede afirmar más. */
export function matchesFilter(facts: ListFacts | null, filter: CollectionFilter): boolean {
    if (!facts) return filter === 'mine';
    if (isClosedCredit(facts)) return false;
    if (filter === 'mine') {
        return (
            facts.status !== LoanCreditStatusEnum.SETTLED &&
            facts.status !== LoanCreditStatusEnum.PENDING_ACTIVATION
        );
    }
    if (filter === 'arrears') return isInArrears(facts);
    return (
        facts.status === LoanCreditStatusEnum.ACTIVE &&
        facts.couponDueIn !== null &&
        facts.couponDueIn >= 0 &&
        facts.couponDueIn <= 7
    );
}

export function filterCounts(
    rows: CollectionCredit[],
    today: string,
): Record<CollectionFilter, number> {
    const facts = rows.map((row) => factsOfRow(row, today));
    return {
        mine: facts.filter((f) => matchesFilter(f, 'mine')).length,
        'due-this-week': facts.filter((f) => matchesFilter(f, 'due-this-week')).length,
        arrears: facts.filter((f) => matchesFilter(f, 'arrears')).length,
    };
}

/** Búsqueda parcial sobre lo que el vendedor ve de su cartera. */
export function matchesSearch(row: CollectionCredit, query: string): boolean {
    const haystack = [row.customerName, row.creditId, row.customerCurp ?? '', row.imei ?? '']
        .join(' ')
        .toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
}

/** Orden por defecto: alfabético por cliente. */
export function sortRows(rows: CollectionCredit[]): CollectionCredit[] {
    return [...rows].sort((a, b) => a.customerName.localeCompare(b.customerName, 'es-MX'));
}

/** La lista izquierda: filtro sobre mis créditos y búsqueda parcial dentro de ellos. */
export function visibleRows(
    rows: CollectionCredit[],
    filter: CollectionFilter,
    query: string,
    today: string,
): CollectionCredit[] {
    const pool = rows.filter((row) => matchesFilter(factsOfRow(row, today), filter));
    const term = query.trim();
    return sortRows(term ? pool.filter((row) => matchesSearch(row, term)) : pool);
}

/** Cómo se busca fuera de la cartera. Solo con un identificador completo que el silo entienda. */
export type WalkInKind = 'credit' | 'phone' | 'imei';

const CREDIT_ID_REGEX = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

export function walkInKindOf(query: string): WalkInKind | null {
    const raw = query.replace(/[\s-]/g, '');
    if (/^\d{10}$/.test(raw)) return 'phone';
    if (/^\d{15}$/.test(raw)) return 'imei';
    if (CREDIT_ID_REGEX.test(query.trim())) return 'credit';
    return null;
}

/** El cupón que se muestra a cobrar, sea de mi cartera o de un walk-in. */
export interface CouponView {
    installmentNumber: number;
    /** Plazo del crédito; el walk-in no lo trae. */
    termWeeks: number | null;
    dueDate: string | undefined;
    /** Días al vencimiento: negativo = vencido hace N días. */
    dueIn: number | null;
    amountCents: number;
}

export function couponViewOf(facts: CreditFacts): CouponView | null {
    const coupon = facts.coupon;
    if (!coupon) return null;
    return {
        installmentNumber: coupon.installmentNumber,
        termWeeks: facts.termWeeks,
        dueDate: coupon.dueDate,
        dueIn: facts.couponDueIn,
        amountCents: owedCents(coupon),
    };
}

/** El cupón de un walk-in sale de la cuota que la búsqueda dice que toca. */
export function walkInCouponOf(lookup: SellerCreditLookupResult, today: string): CouponView | null {
    const next = lookup.nextInstallment;
    if (!next) return null;
    return {
        installmentNumber: next.installmentNumber,
        termWeeks: null,
        dueDate: next.dueDate ?? undefined,
        dueIn: next.dueDate ? daysBetweenDays(installmentDueDay(next.dueDate), today) : null,
        amountCents: next.amountCents,
    };
}

export type StatusTone = 'ok' | 'soon' | 'arrears' | 'settled';

/** La etiqueta de la derecha de cada fila. */
export function statusTagOf(facts: ListFacts): { label: string; tone: StatusTone } {
    if (facts.status === LoanCreditStatusEnum.SETTLED)
        return { label: 'Liquidado', tone: 'settled' };
    if (isInArrears(facts)) {
        return { label: `${String(facts.overdueDays ?? 0)}d mora`, tone: 'arrears' };
    }
    if (facts.couponDueIn !== null && facts.couponDueIn >= 0 && facts.couponDueIn <= 3) {
        return { label: 'Vence pronto', tone: 'soon' };
    }
    return { label: 'Al día', tone: 'ok' };
}

/** Lo que falta pagar de una cuota, parciales descontados. */
export function owedCents(item: LoanInstallmentResponse): number {
    return item.outstandingBalanceCents;
}

/** «vencido hace N días» · «vence HOY» · «vence en N días», como el mock. */
export function couponWhen(dueIn: number): { text: string; tone: 'arrears' | 'today' | 'future' } {
    if (dueIn < 0) {
        const days = -dueIn;
        return {
            text: `vencido hace ${String(days)} día${days === 1 ? '' : 's'}`,
            tone: 'arrears',
        };
    }
    if (dueIn === 0) return { text: 'vence HOY', tone: 'today' };
    return { text: `vence en ${String(dueIn)} día${dueIn === 1 ? '' : 's'}`, tone: 'future' };
}

/** Nivel congelado al originar, en el rótulo del mock. */
export const CLIENT_LEVEL_LABEL: Record<string, string> = {
    GOLD: 'oro',
    SILVER: 'plata',
    BRONZE: 'bronce',
};
